import { useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Wallet, TrendingUp, TrendingDown, DollarSign, Download } from 'lucide-react';
import { GET_REPORTE_FLUJOS_CAJA } from '../../graphql/reportes';
import FiltrosReporte from './FiltrosReporte';
import Loading from '../shared/Loading';
import Button from '../shared/Button';
import './ReporteFlujosCaja.css';

function ReporteFlujosCaja() {
  const [datos, setDatos] = useState(null);
  const [getReporte, { loading, error }] = useLazyQuery(GET_REPORTE_FLUJOS_CAJA, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => setDatos(data.reporteFlujosCaja),
    onError: (err) => console.error('Error al obtener Flujo de Caja:', err)
  });

  const handleFiltrar = ({ fechaDesde, fechaHasta }) => {
    getReporte({ variables: { fechaDesde, fechaHasta } });
  };

  const formatearMoneda = (valor) =>
    new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(valor || 0);

  const formatearPorcentaje = (valor) => `${parseFloat(valor || 0).toFixed(2)}%`;

  return (
    <div className="reporte-flujos-caja">
      <FiltrosReporte onFiltrar={handleFiltrar} loading={loading} />

      {loading && <Loading />}
      {error && <div className="error-message">Error: {error.message}</div>}

      {datos && (
        <>
          {/* Cards resumen */}
          <div className="reporte-cards">
            <div className="reporte-card">
              <div className="reporte-card__icon reporte-card__icon--info">
                <Wallet size={24} />
              </div>
              <div className="reporte-card__content">
                <div className="reporte-card__label">Saldo Inicial</div>
                <div className="reporte-card__value">{formatearMoneda(datos.saldo_inicial)}</div>
              </div>
            </div>
            <div className="reporte-card">
              <div className="reporte-card__icon reporte-card__icon--success">
                <TrendingUp size={24} />
              </div>
              <div className="reporte-card__content">
                <div className="reporte-card__label">Ingresos</div>
                <div className="reporte-card__value">{formatearMoneda(datos.total_ingresos)}</div>
              </div>
            </div>
            <div className="reporte-card">
              <div className="reporte-card__icon reporte-card__icon--danger">
                <TrendingDown size={24} />
              </div>
              <div className="reporte-card__content">
                <div className="reporte-card__label">Egresos</div>
                <div className="reporte-card__value">{formatearMoneda(datos.total_egresos)}</div>
              </div>
            </div>
            <div className="reporte-card">
              <div className={`reporte-card__icon reporte-card__icon--${datos.saldo_final >= 0 ? 'success' : 'danger'}`}>
                <DollarSign size={24} />
              </div>
              <div className="reporte-card__content">
                <div className="reporte-card__label">Saldo Final</div>
                <div className="reporte-card__value">{formatearMoneda(datos.saldo_final)}</div>
              </div>
            </div>
          </div>

          {/* Por método de pago */}
          <div className="reporte-seccion">
            <h3 className="reporte-seccion__titulo">Flujo por Método de Pago</h3>
            <div className="tabla-scroll">
              <table className="reporte-tabla">
                <thead>
                  <tr>
                    <th>Método</th>
                    <th>Ingresos</th>
                    <th>Egresos</th>
                    <th>Neto</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.por_metodo_pago.map(mp => (
                    <tr key={mp.metodo}>
                      <td><strong>{mp.metodo}</strong></td>
                      <td className="text-success">{formatearMoneda(mp.ingresos)}</td>
                      <td className="text-danger">{formatearMoneda(mp.egresos)}</td>
                      <td className={mp.neto >= 0 ? 'text-success' : 'text-danger'}>
                        <strong>{formatearMoneda(mp.neto)}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Top gastos y proveedores */}
          <div className="reporte-grid-2">
            <div className="reporte-seccion">
              <h3 className="reporte-seccion__titulo">Top Gastos por Categoría</h3>
              <div className="tabla-scroll">
                <table className="reporte-tabla">
                  <thead>
                    <tr>
                      <th>Categoría</th>
                      <th>Monto</th>
                      <th>%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.top_gastos.map(g => (
                      <tr key={g.concepto}>
                        <td>{g.concepto}</td>
                        <td>{formatearMoneda(g.monto)}</td>
                        <td>{formatearPorcentaje(g.porcentaje)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="reporte-seccion">
              <h3 className="reporte-seccion__titulo">Top Proveedores</h3>
              <div className="tabla-scroll">
                <table className="reporte-tabla">
                  <thead>
                    <tr>
                      <th>Proveedor</th>
                      <th>Monto</th>
                      <th>%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.top_proveedores.map(p => (
                      <tr key={p.concepto}>
                        <td>{p.concepto}</td>
                        <td>{formatearMoneda(p.monto)}</td>
                        <td>{formatearPorcentaje(p.porcentaje)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Flujo diario */}
          <div className="reporte-seccion">
            <h3 className="reporte-seccion__titulo">Flujo Diario</h3>
            <div className="tabla-scroll">
              <table className="reporte-tabla">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Ingresos</th>
                    <th>Egresos</th>
                    <th>Saldo Acumulado</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.flujo_diario.map(dia => (
                    <tr key={dia.fecha}>
                      <td>{new Date(dia.fecha).toLocaleDateString('es-CO', { timeZone: 'America/Bogota' })}</td>
                      <td className="text-success">{formatearMoneda(dia.ingresos)}</td>
                      <td className="text-danger">{formatearMoneda(dia.egresos)}</td>
                      <td className={dia.saldo_acumulado >= 0 ? 'text-success' : 'text-danger'}>
                        <strong>{formatearMoneda(dia.saldo_acumulado)}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!datos && !loading && (
        <div className="reporte-placeholder">
          <Wallet size={64} />
          <p>Selecciona un rango de fechas para generar el Flujo de Caja</p>
        </div>
      )}
    </div>
  );
}

export default ReporteFlujosCaja;
